import model from './model';
import {
  OptionButtonWidgetComponentIds as ComponentIds,
  OptionButtonWidgetEvents,
  OptionButtonItemComponentIds as ItemComponentIds,
  OptionButtonStates,
} from './config/constants';
import {ChoiceItem, getSelectedChoice, hasEmptySelections, getChoiceById} from '../../utils/optionChoicesUtils';
import {makeAutoObservable} from 'mobx';
import {SPECS} from '../../specs';
import {OptionButtonWidget} from '../../types/widget-overrides';
import {BindAll} from '@wix/velocycle-mobx';

export default model.createController((controllerParams) => {
  const {flowAPI, $props, $widget} = controllerParams;
  const $w = controllerParams.$w as OptionButtonWidget;
  const $bindAll = controllerParams.$bindAll as BindAll<typeof $w>;

  const focusOnItemAt = (index: number) => {
    const itemId = $w(ComponentIds.Repeater).data?.[index]?._id;
    if (itemId) {
      focusOnItemById(itemId);
    }
  };

  const focusOnItemById = (itemId: string) => {
    $w(ComponentIds.Repeater).forItems([itemId], ($item) => {
      $item(ItemComponentIds.SelectableContainer).focus();
    });
  };

  const $widgetState = makeAutoObservable({
    get selections(): ChoiceItem[] {
      return $props.selections;
    },
    get selectedChoice() {
      return getSelectedChoice($props);
    },
    get isEmpty() {
      return hasEmptySelections($props);
    },
  });

  return {
    pageReady: async () => {
      if (!flowAPI.experiments.enabled(SPECS.OptionButtonsUoU)) {
        $w(ComponentIds.TopContainer).collapse();
        return;
      }
      $bindAll({
        [ComponentIds.TopContainer]: {
          hidden: () => $widgetState.selections.length === 0,
        },
        [ComponentIds.Label]: {
          deleted: () => !$props.shouldShowTitle,
          text: () =>
            $widgetState.selectedChoice ? `${$props.label}: ${$widgetState.selectedChoice.description}` : $props.label,
        },
        [ComponentIds.Repeater]: {
          data: () => $props.selections,
          item: ({_id: itemId}: ChoiceItem, _, index) => {
            const $itemState = makeAutoObservable({
              get isDisabled() {
                return !!$itemState.choice?.isDisabled;
              },
              get isSelected() {
                return $widgetState.selectedChoice?._id === itemId;
              },
              get choiceLabel() {
                return $itemState.choice?.description;
              },
              get choiceId() {
                return $itemState.choice?.selectionId;
              },
              get choice() {
                return getChoiceById($widgetState.selections, itemId);
              },
            });
            return {
              [ItemComponentIds.DefaultText]: {
                text: () => $itemState.choiceLabel as string,
              },
              [ItemComponentIds.SelectedText]: {
                text: () => $itemState.choiceLabel as string,
              },
              [ItemComponentIds.MultiStateBox]: {
                currentState: () => ($itemState.isSelected ? OptionButtonStates.Selected : OptionButtonStates.Default),
              },
              [ItemComponentIds.SelectableContainer]: {
                inputLabel: () => $itemState.choiceLabel as string,
              },
            };
          },
          // @ts-expect-error: There is no onItemSelected in $bindAll types
          onItemSelected: (_, {selectionId}) => {
            $widget.fireEvent(OptionButtonWidgetEvents.Change, selectionId);
          },
          accessibility: {
            ariaAttributes: {
              labelledBy: () => $w(ComponentIds.Label),
              describedBy: () => $w(ComponentIds.ErrorMessage).getTextSelector(),
            },
          },
        },
        [ComponentIds.ErrorMessage]: {
          deleted: () => !$props.isError,
          text: () => ($props.isError ? $props.errorMessage : ''),
        },
      });
    },
    exports: {
      focus: () => {
        focusOnItemAt(0);
      },
    },
  };
});
